/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AccountsComponent } from './pages/accounts/accounts.component';
import { AccountsModule } from './pages/accounts/accounts.module';
import { AccountsRecoverKeysComponent } from './pages/accounts/accounts-recover-keys/accounts-recover-keys.component';
import { AccountsRecoverKeysModule } from './pages/accounts/accounts-recover-keys/accounts-recover-keys.module';
import { AccountsSettingsComponent } from './pages/accounts/accounts-settings/accounts-settings.component';
import { AccountsSettingsModule } from './pages/accounts/accounts-settings/accounts-settings.module';
import { AccountsSsoComponent } from './pages/accounts/accounts-sso/accounts-sso.component';
import { AccountsSsoModule } from './pages/accounts/accounts-sso/accounts-sso.module';
import {
    AccountsTwoFactorAuthComponent
} from './pages/accounts/accounts-two-factor-auth/accounts-two-factor-auth.component';
import { AccountsTwoFactorAuthModule } from './pages/accounts/accounts-two-factor-auth/accounts-two-factor-auth.module';
import { AdcAddDetailsComponent } from './pages/adcs/list/addAdc/page2/adc-add-details.component';
import { AdcSelectSupplierComponent } from './pages/adcs/list/addAdc/page1/adc-select-supplier.component';
import { AdcSelectSupplierModule } from './pages/adcs/list/addAdc/page1/adc-select-supplier.module';
import { AdcShuttleModule } from './pages/adcs/shuttle/adcs-shuttle.module';
import { AdcsComponent } from './pages/adcs/adcs.component';
import { AdcsEditComponent } from './pages/adcs/list/edit/adcs.edit.component';
import { AdcsEditModule } from './pages/adcs/list/edit/adcs.edit.module';
import { AdcsListComponent } from './pages/adcs/list/adcs.list.component';
import { AdcsListModule } from './pages/adcs/list/adcs.list.module';
import { AdcsModule } from './pages/adcs/adcs.module';
import { AdcsOverviewComponent } from './pages/adcs/overview/adcs.overview.component';
import { AdcsOverviewModule } from './pages/adcs/overview/adcs.overview.module';
import { AdcsShuttleComponent } from './pages/adcs/shuttle/adcs-shuttle.component';
import { AdcsUtilsService, NamespaceService, PortalRbacService } from './shared/services/utils';
import { AddEditNamespaceComponent } from './pages/organisation/namespaces/AddEditNamespace/add-edit-namespace.component';
import {
    AddEditPaymentMethodComponent
} from './pages/organisation/billing/add-edit-payment-method/add-edit-payment-method.component';
import {
    AddEditPaymentMethodModule
} from './pages/organisation/billing/add-edit-payment-method/add-edit-payment-method.module';
import { AddEditSubnetComponent } from './pages/adcs/shuttle/edit-shuttle/add-edit-subnet/add-edit-subnet.component';
import { AuditingComponent } from './pages/organisation/security-group/auditing/auditing.component';
import { AuthGuard, EnvironmentGuard, HasOrgIdGuard, RbacPageGuard, SocketGuard } from '../../core/guards';
import { BackupDetailsComponent } from './pages/storage/backups/backupDetails/backup-details.component';
import { BackupDetailsModule } from './pages/storage/backups/backupDetails/backup-details.module';
import { BillingOverviewComponent } from './pages/organisation/billing/overview/billing-overview.component';
import { BillingOverviewModule } from './pages/organisation/billing/overview/billing-overview.module';
import { BillingResolvers } from './shared/resolvers/billing.resolvers';
import { CentraliseManagementSharedModule } from './shared/centraliseManagement.shared.module';
import { CentralisedManagementComponent } from './centralisedManagement.component';
import { CertificatesComponent } from './pages/security/certificates/certificates.component';
import {
    CertificatesDetailsComponent
} from './pages/security/certificates/certificates-details/certificates-details.component';
import { CheckoutComponent } from './pages/organisation/billing/checkout-page/checkout.component';
import { CheckoutModule } from './pages/organisation/billing/checkout-page/checkout.module';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { EditShuttleComponent } from './pages/adcs/shuttle/edit-shuttle/edit-shuttle.component';
import { EditShuttleModule } from './pages/adcs/shuttle/edit-shuttle/edit-shuttle.module';
import { InsightDetailsComponent } from './pages/security/insights/insight-details/insight-details.component';
import { InsightsComponent } from './pages/security/insights/insights.component';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { NamespaceComponent } from './shared/components';
import { NamespaceResolvers } from './shared/resolvers/namespace-resolver.service';
import { NamespacesComponent } from './pages/organisation/namespaces/namespaces.component';
import { NamespacesModule } from './pages/organisation/namespaces/namespaces.module';
import { NgOptimizedImage } from '@angular/common';
import { NoAccessComponent, PageErrorComponent } from '../../core/components';
import { OnboardComponent } from './pages/onboard/onboard.component';
import { OnboardModule } from './pages/onboard/onboard.module';
import { OrganisationComponent } from './pages/organisation/organisation.component';
import { OrganisationModule } from './pages/organisation/organisation.module';
import { OrganisationResolvers } from './shared/resolvers/organisation-resolver.service';
import {
    OrganisationSettingsComponent
} from './pages/organisation/organisation-settings/organisation-settings.component';
import { OrganisationSettingsModule } from './pages/organisation/organisation-settings/organisation-settings.module';
import { PipelineComponent } from './pages/scheduler/pipelines/list/pipeline/pipeline.component';
import { PipelineModule } from './pages/scheduler/pipelines/list/pipeline/pipeline.module';
import { PipelinesListComponent } from './pages/scheduler/pipelines/list/pipelines-list.component';
import { PipelinesListModule } from './pages/scheduler/pipelines/list/pipelines-list.module';
import { PortalAddEditGroupComponent } from './pages/organisation/users/addEditGroups/portal-add-edit-group.component';
import { PortalAddEditGroupsModule } from './pages/organisation/users/addEditGroups/portal-add-edit-groups.module';
import { PortalAddEditRolesComponent } from './pages/organisation/users/addEditRoles/portal-add-edit-roles.component';
import { PortalAddEditRolesModule } from './pages/organisation/users/addEditRoles/portal-add-edit-roles.module';
import { PortalAddEditUsersComponent } from './pages/organisation/users/addEditUsers/portal-add-edit-users.component';
import { PortalImportUsersComponent } from './pages/organisation/users/import-users/portal-import-users.component';
import { PortalImportUsersModule } from './pages/organisation/users/import-users/portal-import-users.module';
import { PortalResolverGuard } from './shared/guards/portal-resolver-guard.service';
import { PortalUsersListComponent } from './pages/organisation/users/users-list/portal-users-list.component';
import { PortalUsersListModule } from './pages/organisation/users/users-list/portal-users-list.module';
import { RbacResolvers } from './shared/resolvers/rbac-resolver.service';
import { ResourcesComponent } from './pages/resources/resources.component';
import { ResourcesModule } from './pages/resources/resources.module';
import { ResourcesResolver } from './shared/resolvers/resources-resolver.service';
import { RouterModule } from '@angular/router';
import { SamlComponent } from './pages/organisation/security-group/saml/saml.component';
import { ScheduleComponent } from './pages/scheduler/schedule.component';
import { ScheduleModule } from './pages/scheduler/schedule.module';
import { ScheduleOverviewComponent } from './pages/scheduler/overview/schedule-overview.component';
import { ScheduleOverviewModule } from './pages/scheduler/overview/schedule-overview.module';
import { SchedulerComponent } from './pages/scheduler/scheduler/scheduler.component';
import { SchedulerModule } from './pages/scheduler/scheduler/scheduler.module';
import { SchedulesComponent } from './pages/scheduler/schedules/schedules.component';
import { SchedulesListResolvers } from './shared/resolvers/schedule-list-resolver.service';
import { SchedulesModule } from './pages/scheduler/schedules/schedules.module';
import { SecurityComponent } from './pages/security/security.component';
import { SecurityDashboardComponent } from './pages/security/security-dashboard/security-dashboard.component';
import { SecurityGroupComponent } from './pages/organisation/security-group/security-group.component';
import { SecurityGroupModule } from './pages/organisation/security-group/security-group.module';
import { SecurityOverviewComponent } from './pages/security/security-overview/security-overview.component';
import { SelectPlatformComponent } from './pages/resources/downloads/page2/select-platform.component';
import { SelectPlatformModule } from './pages/resources/downloads/page2/select-platform.module';
import { SelectShuttleComponent } from './pages/resources/downloads/page1/select-shuttle.component';
import { SelectShuttleModule } from './pages/resources/downloads/page1/select-shuttle.module';
import { SharedModule } from '../../core/shared.module';
import { ShuttleAddDetailsComponent } from './pages/adcs/shuttle/addShuttle/page2/shuttle-add-details.component';
import {
    ShuttleAddSelectSupplierComponent
} from './pages/adcs/shuttle/addShuttle/page1/shuttle-add-select-supplier.component';
import { StorageBackupsComponent } from './pages/storage/backups/storage.backups.component';
import { StorageBackupsModule } from './pages/storage/backups/storage.backups.module';
import { StorageComponent } from './pages/storage/storage.component';
import { StorageListResolvers } from './shared/resolvers/storage-list-resolver.service';
import { StorageModule } from './pages/storage/storage.module';
import { StorageOverviewComponent } from './pages/storage/overview/storage.overview.component';
import { StorageOverviewModule } from './pages/storage/overview/storage.overview.module';
import { SubscriptionComponent } from './pages/organisation/subscription/subscription.component';
import { SubscriptionModule } from './pages/organisation/subscription/subscription.module';
import { SupportComponent } from './pages/support/support.component';
import { SupportModule } from './pages/support/support.module';
import { TicketConversationComponent } from './pages/support/ticketConversation/ticket-conversation.component';
import { TicketConversationModule } from './pages/support/ticketConversation/ticket-conversation.module';
import { TicketCreateComponent } from './pages/support/ticketCreate/ticket-create.component';
import { TicketCreateModule } from './pages/support/ticketCreate/ticket-create.module';
import { TicketListComponent } from './pages/support/ticketList/ticket-list.component';
import { TicketListModule } from './pages/support/ticketList/ticket-list.module';
import { UserListResolvers } from './shared/resolvers/user-list-resolver.service';

/**
 * Routes associated to the Login Page/Component
 * @type {ModuleWithProviders}
 */
const CentralisedManagementRouting: ModuleWithProviders<any> = RouterModule.forChild([
    {
        path: 'cm/onboard',
        component: OnboardComponent,
        canActivate: [AuthGuard, SocketGuard, EnvironmentGuard],
    },
    {
        path: 'cm',
        component: CentralisedManagementComponent,
        canActivate: [AuthGuard, SocketGuard, EnvironmentGuard, HasOrgIdGuard, PortalResolverGuard],
        children: [
            {
                path: 'accounts',
                component: AccountsComponent,
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'sso'
                    },
                    {
                        path: 'recover-keys',
                        canActivate: [RbacPageGuard],
                        component: AccountsRecoverKeysComponent
                    },
                    {
                        path: 'settings',
                        canActivate: [RbacPageGuard],
                        component: AccountsSettingsComponent
                    },
                    {
                        path: 'sso',
                        canActivate: [RbacPageGuard],
                        component: AccountsSsoComponent
                    },
                    {
                        path: 'two-factor-auth',
                        canActivate: [RbacPageGuard],
                        component: AccountsTwoFactorAuthComponent
                    },
                    {
                        path: 'no-access',
                        component: NoAccessComponent
                    },
                    {
                        path: 'page-error',
                        component: PageErrorComponent
                    }
                ]
            },
            {
                path: 'adcs',
                component: AdcsComponent,
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'overview'
                    },
                    {
                        path: 'overview',
                        component: AdcsOverviewComponent,
                    },
                    {
                        path: 'list',
                        children: [
                            {
                                path: '',
                                canActivate: [RbacPageGuard],
                                component: AdcsListComponent,
                            },
                            {
                                path: 'select-vendor',
                                canActivate: [RbacPageGuard],
                                component: AdcSelectSupplierComponent,
                            },
                            {
                                path: 'add/:vendorId',
                                canActivate: [RbacPageGuard],
                                component: AdcAddDetailsComponent,
                            },
                            {
                                path: 'edit/:id/:namespaceId',
                                canActivate: [RbacPageGuard],
                                component: AdcsEditComponent,
                            },
                            {
                                path: 'no-access',
                                component: NoAccessComponent
                            },
                            {
                                path: 'page-error',
                                component: PageErrorComponent
                            }
                        ]
                    },
                    {
                        path: 'shuttle',
                        children: [
                            {
                                path: '',
                                canActivate: [RbacPageGuard],
                                component: AdcsShuttleComponent,
                            },
                            {
                                path: 'add',
                                canActivate: [RbacPageGuard],
                                component: ShuttleAddSelectSupplierComponent,
                            },
                            {
                                path: 'add/:vendorId',
                                canActivate: [RbacPageGuard],
                                component: ShuttleAddDetailsComponent,
                            },
                            {
                                path: 'edit/:id',
                                canActivate: [RbacPageGuard],
                                component: EditShuttleComponent,
                            },
                            {
                                path: 'add-subnet/:shuttleId',
                                component: AddEditSubnetComponent,
                            },
                            {
                                path: 'edit-subnet/:shuttleId/:subnetId',
                                component: AddEditSubnetComponent,
                            },
                            {
                                path: 'no-access',
                                component: NoAccessComponent
                            },
                            {
                                path: 'page-error',
                                component: PageErrorComponent
                            }
                        ]
                    }
                ]
            },
            {
                path: 'organisation',
                component: OrganisationComponent,
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'users'
                    },
                    {
                        path: 'billing',
                        children: [
                            {
                                path: 'overview',
                                canActivate: [RbacPageGuard],
                                component: BillingOverviewComponent,
                            },
                            {
                                path: 'checkout',
                                canActivate: [RbacPageGuard],
                                component: CheckoutComponent,
                            },
                            {
                                path: 'checkout/:param',
                                canActivate: [RbacPageGuard],
                                component: CheckoutComponent,
                            },
                            {
                                path: 'payment-method',
                                canActivate: [RbacPageGuard],
                                component: AddEditPaymentMethodComponent,
                            },
                            {
                                path: 'no-access',
                                component: NoAccessComponent
                            },
                            {
                                path: 'page-error',
                                component: PageErrorComponent
                            }
                        ]
                    },
                    {
                        path: 'namespaces',
                        children: [
                            {
                                path: '',
                                canActivate: [RbacPageGuard],
                                component: NamespacesComponent,
                            },
                            {
                                path: 'add',
                                canActivate: [RbacPageGuard],
                                component: AddEditNamespaceComponent,
                            },
                            {
                                path: 'edit/:id',
                                canActivate: [RbacPageGuard],
                                component: AddEditNamespaceComponent,
                            },
                            {
                                path: 'no-access',
                                component: NoAccessComponent
                            },
                            {
                                path: 'page-error',
                                component: PageErrorComponent
                            }
                        ]
                    },
                    {
                        path: 'security-group',
                        component: SecurityGroupComponent,
                        children: [
                            {
                                path: '',
                                pathMatch: 'full',
                                redirectTo: 'auditing'
                            },
                            {
                                path: 'auditing',
                                canActivate: [RbacPageGuard],
                                component: AuditingComponent,
                            },
                            {
                                path: 'saml',
                                canActivate: [RbacPageGuard],
                                component: SamlComponent,
                            },
                            {
                                path: 'no-access',
                                component: NoAccessComponent
                            },
                            {
                                path: 'page-error',
                                component: PageErrorComponent
                            }
                        ]
                    },
                    {
                        path: 'settings',
                        canActivate: [RbacPageGuard],
                        component: OrganisationSettingsComponent,
                    },
                    {
                        path: 'subscriptions',
                        component: SubscriptionComponent,
                    },
                    {
                        path: 'users',
                        children: [
                            {
                                path: '',
                                pathMatch: 'full',
                                redirectTo: 'list'
                            },
                            {
                                path: 'list',
                                canActivate: [RbacPageGuard],
                                component: PortalUsersListComponent,
                            },
                            {
                                path: 'user/add',
                                canActivate: [RbacPageGuard],
                                component: PortalAddEditUsersComponent,
                            },
                            {
                                path: 'user/edit/:id',
                                canActivate: [RbacPageGuard],
                                component: PortalAddEditUsersComponent,
                            },
                            {
                                path: 'user/import',
                                canActivate: [RbacPageGuard],
                                component: PortalImportUsersComponent,
                            },
                            {
                                path: 'role/add',
                                canActivate: [RbacPageGuard],
                                component: PortalAddEditRolesComponent,
                            },
                            {
                                path: 'role/edit/:id',
                                canActivate: [RbacPageGuard],
                                component: PortalAddEditRolesComponent,
                            },
                            {
                                path: 'role/edit/:id/:duplicate',
                                canActivate: [RbacPageGuard],
                                component: PortalAddEditRolesComponent,
                            },
                            {
                                path: 'group/add',
                                canActivate: [RbacPageGuard],
                                component: PortalAddEditGroupComponent,
                            },
                            {
                                path: 'group/edit/:id',
                                canActivate: [RbacPageGuard],
                                component: PortalAddEditGroupComponent,
                            },
                            {
                                path: 'no-access',
                                component: NoAccessComponent
                            },
                            {
                                path: 'page-error',
                                component: PageErrorComponent
                            }
                        ]
                    },
                    {
                        path: 'no-access',
                        component: NoAccessComponent
                    },
                    {
                        path: 'page-error',
                        component: PageErrorComponent
                    }
                ]
            },
            {
                path: 'resources',
                component: ResourcesComponent,
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'downloads'
                    },
                    {
                        path: 'downloads',
                        canActivate: [RbacPageGuard],
                        component: SelectShuttleComponent,
                    },
                    {
                        path: 'platform/:shuttleName',
                        component: SelectPlatformComponent,
                    },
                    {
                        path: 'no-access',
                        component: NoAccessComponent
                    },
                    {
                        path: 'page-error',
                        component: PageErrorComponent
                    }
                ]
            },
            {
                path: 'scheduler',
                component: ScheduleComponent,
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'overview'
                    },
                    {
                        path: 'overview',
                        component: ScheduleOverviewComponent,
                    },
                    {
                        path: 'pipelines',
                        children: [
                            {
                                path: '',
                                canActivate: [RbacPageGuard],
                                component: PipelinesListComponent,
                            },
                            {
                                path: ':id',
                                canActivate: [RbacPageGuard],
                                component: PipelineComponent,
                            },
                            {
                                path: 'no-access',
                                component: NoAccessComponent
                            },
                            {
                                path: 'page-error',
                                component: PageErrorComponent
                            }
                        ]
                    },
                    {
                        path: 'schedules',
                        children: [
                            {
                                path: '',
                                pathMatch: 'full',
                                canActivate: [RbacPageGuard],
                                component: SchedulesComponent,
                            },
                            {
                                path: 'new',
                                canActivate: [RbacPageGuard],
                                component: SchedulerComponent,
                            },
                            {
                                path: 'edit',
                                canActivate: [RbacPageGuard],
                                component: SchedulerComponent,
                            },
                            {
                                path: 'no-access',
                                component: NoAccessComponent
                            },
                            {
                                path: 'page-error',
                                component: PageErrorComponent
                            }
                        ]
                    },
                    {
                        path: 'no-access',
                        component: NoAccessComponent
                    },
                    {
                        path: 'page-error',
                        component: PageErrorComponent
                    }
                ]
            },
            {
                path: 'storage',
                component: StorageComponent,
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'overview'
                    },
                    {
                        path: 'overview',
                        component: StorageOverviewComponent,
                    },
                    {
                        path: 'backups',
                        children: [
                            {
                                path: 'no-access',
                                component: NoAccessComponent
                            },
                            {
                                path: 'page-error',
                                component: PageErrorComponent
                            },
                            {
                                path: '',
                                canActivate: [RbacPageGuard],
                                component: StorageBackupsComponent,
                            },
                            {
                                path: ':id',
                                canActivate: [RbacPageGuard],
                                component: BackupDetailsComponent
                            }
                        ]
                    },
                    {
                        path: 'no-access',
                        component: NoAccessComponent
                    },
                    {
                        path: 'page-error',
                        component: PageErrorComponent
                    }
                ]
            },
            {
                path: 'security',
                component: SecurityComponent,
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'overview'
                    },
                    {
                        path: 'no-access',
                        component: NoAccessComponent
                    },
                    {
                        path: 'page-error',
                        component: PageErrorComponent
                    },
                    {
                        path: 'overview',
                        component: SecurityOverviewComponent,
                    },
                    {
                        path: 'dashboard',
                        component: SecurityDashboardComponent,
                    },
                    {
                        path: 'insights',
                        children: [
                            {
                                path: '',
                                pathMatch: 'full',
                                canActivate: [RbacPageGuard],
                                component: InsightsComponent,
                            },
                            {
                                path: 'details/:id',
                                canActivate: [RbacPageGuard],
                                component: InsightDetailsComponent,
                            },
                            {
                                path: 'no-access',
                                component: NoAccessComponent
                            },
                            {
                                path: 'page-error',
                                component: PageErrorComponent
                            }
                        ]
                    },
                    {
                        path: 'certificates',
                        children: [
                            {
                                path: '',
                                pathMatch: 'full',
                                canActivate: [RbacPageGuard],
                                component: CertificatesComponent
                            },
                            {
                                path: 'details/:id',
                                canActivate: [RbacPageGuard],
                                component: CertificatesDetailsComponent,
                            },
                            {
                                path: 'no-access',
                                component: NoAccessComponent
                            },
                            {
                                path: 'page-error',
                                component: PageErrorComponent
                            }
                        ]
                    }
                ]
            },
            {
                path: 'support',
                component: SupportComponent,
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'tickets'
                    },
                    {
                        path: 'tickets',
                        canActivate: [RbacPageGuard],
                        component: TicketListComponent,
                    },
                    {
                        path: 'create-ticket',
                        canActivate: [RbacPageGuard],
                        component: TicketCreateComponent,
                    },
                    {
                        path: 'conversation/:id',
                        canActivate: [RbacPageGuard],
                        component: TicketConversationComponent,
                    },
                    {
                        path: 'no-access',
                        component: NoAccessComponent
                    },
                    {
                        path: 'page-error',
                        component: PageErrorComponent
                    }
                ]
            },
            {
                path: 'no-access',
                component: NoAccessComponent
            },
            {
                path: 'page-error',
                component: PageErrorComponent
            },
            {
                path: 'dashboard',
                component: DashboardComponent
            },
            {
                path: '**',
                redirectTo: '/404',
            }
        ]
    }
]);

/**
 * Define dependencies
 */
@NgModule({
    imports: [
        CentraliseManagementSharedModule,
        CentralisedManagementRouting,
        SharedModule,
        // Pages
        AccountsModule,
        AccountsRecoverKeysModule,
        AccountsSettingsModule,
        AccountsSsoModule,
        AccountsTwoFactorAuthModule,
        AdcSelectSupplierModule,
        AdcShuttleModule,
        AdcsEditModule,
        AdcsListModule,
        AdcsListModule,
        AdcsModule,
        AdcsOverviewModule,
        AddEditPaymentMethodModule,
        BackupDetailsModule,
        BillingOverviewModule,
        CheckoutModule,
        EditShuttleModule,
        NamespacesModule,
        NgOptimizedImage,
        OnboardModule,
        OrganisationModule,
        OrganisationSettingsModule,
        PipelineModule,
        PipelinesListModule,
        PortalAddEditGroupsModule,
        PortalAddEditRolesModule,
        PortalImportUsersModule,
        PortalUsersListModule,
        ResourcesModule,
        ScheduleModule,
        ScheduleOverviewModule,
        SchedulerModule,
        SchedulesModule,
        SecurityGroupModule,
        SelectPlatformModule,
        SelectShuttleModule,
        StorageBackupsModule,
        StorageModule,
        StorageOverviewModule,
        SubscriptionModule,
        SupportModule,
        TicketConversationModule,
        TicketCreateModule,
        TicketListModule
    ],
    declarations: [
        CentralisedManagementComponent,
        CertificatesComponent,
        CertificatesDetailsComponent,
        DashboardComponent,
        InsightDetailsComponent,
        InsightsComponent,
        NamespaceComponent,
        NoAccessComponent,
        NoAccessComponent,
        PageErrorComponent,
        SecurityComponent,
        SecurityDashboardComponent
    ],
    exports: [
        CentraliseManagementSharedModule
    ],
    providers: [
        AdcsUtilsService,
        AuthGuard,
        BillingResolvers,
        NamespaceResolvers,
        NamespaceService,
        OrganisationResolvers,
        PortalRbacService,
        RbacResolvers,
        ResourcesResolver,
        SchedulesListResolvers,
        StorageListResolvers,
        UserListResolvers
    ]
})

export class CentralisedManagementModule {
}
